import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Subscription } from 'rxjs';
import { ToastService } from '@app/services/toast.service';
import { TourGuideService } from '@app/services/tour-guide.service';
import dummyData from '@app/assets/api-dummy-data.json';
import { Keys, Labels } from '@app/util/labels.util';
import { ConfigBuilderService } from '@app/services/config-builder.service';
import { Messages } from '@app/util/messages.util';
import { TourGuideContent } from '@app/util/tour-guide-content.util';
import { TriggerAndArtifactComponent } from '@app/shared/components/config-builder-components/trigger-and-artifact/trigger-and-artifact.component';
import { DynamicDropdownService } from '@app/services/dynamic-dropdown.service';
import { CommonFunctionalitiesService } from '@app/services/common-functionalities.service';
import { AuthService } from '@app/services/auth/auth.service';
import { WfQueueManagerService } from '@app/services/wf-queue-manager.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

@Component({
  selector: 'app-workflow-config',
  templateUrl: './workflow-config.component.html',
  styleUrls: ['./workflow-config.component.scss']
})
export class WorkflowConfigComponent implements OnInit, OnChanges, OnDestroy {
  private readonly defaultLabelName = 'Optimus Quality Gate Check';
  inputArgumentList: any = [];
  workflowData: any = [];
  isInputArgValid: boolean = false;
  isIssueTmplValid: boolean = false;
  tourGuideIds = TourGuideContent;
  dataSubscription: Subscription;
  thresholdList: any;
  selectedTabIndex: number = 0;
  changeEventStatus: boolean = false;
  private _triggerComp: TriggerAndArtifactComponent;
  workflowForm: FormGroup
  isTriggerFormValid: boolean;
  changedEventValue: number;
  workflowConfigObj: any = {}
  wfhIdFromDropdown: any;
  wfData: any[] = [];
  updQgCheckFlag: boolean = false;
  isSoleFlag: boolean = false;

  // Labels used in add-config html
  cfgName: String = Labels.cfgName;
  chWorkflow: String = Labels.chWorkflow;

  // Issue template variables
  workflowDtls: any; // Holds workflow details for issue template
  isIssueTmplChecked: boolean = false; // Flag to indicate if issue template is selected
  issueTmplInputs: any[] = []; // Inputs for the selected issue template
  issueTmplId: number | null; // ID of the selected issue template

  // Labeled checks
  labelList: any[] = [];
  filteredLabelList: any[] = [];
  labelSearchCtrl = new FormControl('');
  selectedLabelId: any = null;

  // Outputs
  @Output() addModeUpdateEvent = new EventEmitter<any>();
  @Output() removeInactiveEvent = new EventEmitter<number>();
  @Output() isAddConfigEvent = new EventEmitter<boolean>();
  @Output() isExpandable = new EventEmitter<any>();
  @Output() cancelUpdClicked = new EventEmitter<any>();

  // Inputs
  @Input() addMode!: boolean;
  @Input() mode: any;
  @Input() isAddConfig!: boolean;
  @Input() configHide: boolean;
  @Input() isEditable: boolean;
  @Input() element: any;
  @Input() cngName: any;
  @Input() cngStatus: any;
  @Input() cngNameUpdValid: any;
  @Input() targetExec: any;
  @Input() jobId: any;

  /**
   * Input object passed from manual workflow trigger page
   * When manualWf?.isManualWfTrigger is true display only input-and-arguments component
  */
  @Input() manualWf: any | undefined;

  /** Specify view childs  */
  @ViewChild(TriggerAndArtifactComponent) set triggerComp(content: TriggerAndArtifactComponent) {
    if (content) {
      this._triggerComp = content;
    }
  };


  /**
   * constructor
   * @param configBuilderService  - An instance of ConfigBuilderService used for managing workflow configurations.
   * @param dpService - An instance of DynamicDropdownService for handling dynamic dropdowns.
   * @param configBuilderService - An instance of ConfigBuilderService used for configuration building.
   * @param fb - An instance of FormBuilder for creating and managing forms.
   * @param tourGuideService - An instance of TourGuideService for managing the tour guide.
   * @param toastService - An instance of ToastService for displaying toast messages.
   * @param commonService - An instance of CommonFunctionalitiesService for common functionalities.
   * @param authService - An instance of AuthService for authentication-related operations.
   * @param wfQueueService - An instance of WfQueueManagerService for managing workflow queues.
  */
  constructor(public configBuilderService: ConfigBuilderService,
    private fb: FormBuilder,
    public tourGuideService: TourGuideService,
    private toastService: ToastService,
    public dpService: DynamicDropdownService,
    public commonService: CommonFunctionalitiesService,
    public authService: AuthService,
    public wfQueueService: WfQueueManagerService,
    public dialog: MatDialog,
    private router: Router) {
    /**
     * Subscribe to data changes in the TourGuideService
     */
    this.dataSubscription = this.tourGuideService.data$.subscribe((res: any) => {
      // If res is true, show the configuration panel and open the matSelect
      if (res === true) {
        this.configHide = false;
      }

      // If there is a tabChange in res, update the selectedTabIndex
      if (res?.tabChange === 'tabChange') {
        this.selectedTabIndex = res.index;
      }

      // If there is a configSave in res, initialize the workflowForm with empty values
      if (res?.configSave) {
        this.workflowForm = this.fb.group({ cngName: '', wfhId: '' });
      }
    });
  }

  // Getter for the trigger component
  get triggerComp() {
    return this._triggerComp;
  }

  /**
   * Lifecycle hook called when the component is initialized.
   * It initializes the form, fetches data based on conditions, and handles workflow changes.
  */
  ngOnInit() {
    this.dpService.dataMap = {} //Reset dynamic dropdown payload
    // Initialize the form used in the component
    this.buildForm();
    this.registerLabelSearchListener();

    // If the tour is starting, set initial data from dummy data for demonstration purposes
    if (this.tourGuideService.isStartTour) {
      // Set workflowData, inputArgumentList, and thresholdList from dummy data
      this.workflowForm.get('cngName')?.patchValue("Test-Config")
      this.workflowData = dummyData.configBuilder.workflow.wfDataDropdown;
      this.inputArgumentList = dummyData.configBuilder.workflow.inputArgs;
      this.thresholdList = dummyData.configBuilder.workflow.thresholdData
      this.mode = 'val';
      return; // Exit the method
    }
    this.tourGuideService.dashboardIndex = 0;

    // If not in tour mode and the component is not editable, fetch all workflows
    if (!this.isEditable) {
      this.getAllWorkFlows();
      this.fetchLabelList();
      return;
    }

    // manual workflow trigger
    if (this.manualWf?.isManualWfTrigger && this.isEditable) {
      this.onWorkflowChange();
      return;
    }

    this.getWorkflowDetails();
    this.fetchLabelList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-evaluate the labeled check enabled/disabled state when the mode switches (e.g. expand -> edit).
    if (changes['mode'] && !changes['mode'].firstChange) {
      this.applyLabelControlState();
    }
  }

  /**
   * handles the event when a workflow is selected from the dynamic dropdown.
   * 
   * @param data dropdown value
   */
  onDynamicDropdownChange(data: any) {
    if (data) {
        // If data is received, update the workflow details
        this.workflowDtls = [data.repoUrl, data.branchName];

        // Remove the existing 'argumentsArray' control from the workflow form
        this.workflowForm.removeControl('argumentsArray');

        // Add a new 'argumentsArray' control as a FormArray to the workflow form
        this.workflowForm.addControl('argumentsArray', new FormArray([]))
        this.wfData.splice(0, this.wfData.length, data?.org);
        this.configBuilderService.setData(this.wfData);
        this.workflowForm?.get('wfhId')?.setValue(data.key, { emitEvent: false });
        this.wfhIdFromDropdown = data?.value;
        this.onWorkflowChange(data)
      }
  }

  /**
   * Lifecycle hook called when the component is about to be destroyed.
   * Unsubscribes from the 'dataSubscription' to prevent memory leaks and resource cleanup.
   */
  ngOnDestroy(): void {
    this.dataSubscription.unsubscribe();
  }

  /** Fetch all labeled checks from the API */
  fetchLabelList(): void {
    this.configBuilderService.getLabelList().subscribe({
      next: (res: any) => {
        const raw = res?.data?.dbResp ?? res?.data ?? res;
        this.labelList = Array.isArray(raw) ? this.sortLabelsAlphabetically(raw) : [];
        this.filteredLabelList = this.labelList.slice();
        this.syncSelectedLabelFromConfig();
        this.applyDefaultLabelForAddConfig();
        this.applyLabelControlState();
      },
      error: () => {
        this.toastService.showError('Error', 'Failed to load labeled checks.');
      }
    });
  }

  /** Disable the labeled check field only in read/expand mode; keep it editable otherwise. */
  private applyLabelControlState(): void {
    if (this.mode === 'read') {
      this.labelSearchCtrl.disable({ emitEvent: false });
    } else {
      this.labelSearchCtrl.enable({ emitEvent: false });
    }
  }

  private applyDefaultLabelForAddConfig(): void {
    if (this.isEditable) return;

    if (this.selectedLabelId !== null && this.selectedLabelId !== undefined && this.selectedLabelId !== '') {
      return;
    }

    const currentValue: any = this.labelSearchCtrl.value;
    const currentSearch = (typeof currentValue === 'string' ? currentValue : currentValue?.name ?? '').trim();
    if (currentSearch) return;

    const defaultLabel = this.labelList.find((label: any) =>
      String(label?.name ?? '').trim().toLowerCase() === this.defaultLabelName.toLowerCase()
    );

    if (!defaultLabel) return;

    this.selectedLabelId = this.getLabelOptionId(defaultLabel);
    this.labelSearchCtrl.setValue(defaultLabel?.name ?? '', { emitEvent: false });
  }

  /**
   * Retrieves workflow details based on the current element's value.
   * - Calls the ConfigBuilderService to fetch workflow details.
   */
  async getWorkflowDetails(): Promise<void> {
    (await this.configBuilderService.getWfDetails(this.element?.value)).subscribe({
      next: (res: any) => {
        // If the response contains workflow details, update the workflowConfigObj and issueTmplInputs
        this.workflowConfigObj = res.data?.dbResp;
        this.syncSelectedLabelFromConfig();

        // If the workflowConfigObj contains issueTempId, process the issue template inputs
        if (this.workflowConfigObj?.issueTempId) {
          // handleDropdown function to set default value for dropdown inputs
          const handleDropdown = (issueInput: any, value: any) => {
            const index = issueInput.attributes.options.indexOf(value?.toString());
            issueInput.attributes.default = index !== -1 ? index : 0; // set default to 0 if value not found
          }

          // Iterate through the arguments in workflowConfigObj
          this.workflowConfigObj.arguments.forEach((input: any) => {
            // Map arguments value to issue template inputs value
            const updatedIssueTmplInputs = this.workflowConfigObj.issueTempInputs.details.map((issueInput: any) => {
              if (!issueInput.attributes) issueInput.attributes = {};

              let value: any = '';
              // Check if input is a JSON object or a simple value
              if (this.commonService.isJSONObject(input.default)) {
                const object_Arr = Object.entries(JSON.parse(input?.value || input.default));
                // If input is a JSON object, parse it and find the value for the issueInput key
                const obj: any = object_Arr.find((obj) => obj[0].toLowerCase() === issueInput.id.toLowerCase());

                if (obj) {
                  // If issueInput is a dropdown, handle it accordingly
                  issueInput.type === 'dropdown' ? handleDropdown(issueInput, obj[1]) : value = obj[1] || '';
                }
              } else {
                // If input is not a JSON object, find the value directly
                const argument = this.workflowConfigObj.arguments.find((arg: { key: string; }) => arg.key.toLowerCase() === issueInput.id.toLowerCase());
                issueInput.type === 'dropdown' ? handleDropdown(issueInput, argument?.value) : value = argument?.value || '';
              }

              // Set the value for the issueInput and return the updated issueInput
              issueInput.attributes.value = value;
              return issueInput;
            });

            // Update the issueTmplInputs with the processed inputs
            this.issueTmplInputs = updatedIssueTmplInputs;
          });
        }

        this.issueTmplId = this.workflowConfigObj?.issueTempId;
        if (this.isEditable) {
          this.editModeWfChange();
        }
      },
      error: (err: any) => {
        console.error(err);
      }
    });
  }

  private syncSelectedLabelFromConfig(): void {
    if (!this.isEditable || !this.workflowConfigObj) return;

    const cfgLabelId = this.workflowConfigObj?.labelId;
    if (cfgLabelId === null || cfgLabelId === undefined || cfgLabelId === '') return;

    // Keep the option value type from label list to ensure mat-select preselects correctly.
    if (Array.isArray(this.labelList) && this.labelList.length > 0) {
      const matched = this.labelList.find((label: any) => String(this.getLabelOptionId(label)) === String(cfgLabelId));
      this.selectedLabelId = matched ? this.getLabelOptionId(matched) : cfgLabelId;
      this.labelSearchCtrl.setValue(matched?.name ?? '', { emitEvent: false });
      return;
    }

    this.selectedLabelId = cfgLabelId;
  }

  private registerLabelSearchListener(): void {
    this.dataSubscription.add(
      this.labelSearchCtrl.valueChanges.subscribe((value: any) => {
        const search = typeof value === 'string' ? value : value?.name ?? '';
        this.filteredLabelList = this.filterLabelList(search);

        const exactMatch = this.labelList.find((label: any) =>
          String(label?.name ?? '').trim().toLowerCase() === search.trim().toLowerCase()
        );

        this.selectedLabelId = exactMatch ? this.getLabelOptionId(exactMatch) : null;
      })
    );
  }

  private sortLabelsAlphabetically(labels: any[]): any[] {
    return [...labels].sort((first, second) =>
      String(first?.name ?? '').localeCompare(String(second?.name ?? ''), undefined, { sensitivity: 'base' })
    );
  }

  private filterLabelList(search: string): any[] {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return this.labelList.slice();
    }

    return this.labelList.filter((label: any) =>
      String(label?.name ?? '').toLowerCase().includes(normalizedSearch)
    );
  }

  displayLabelName(label: any): string {
    if (!label) return '';
    return typeof label === 'string' ? label : (label?.name ?? '');
  }

  onLabelSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedLabel = event.option.value;
    this.selectedLabelId = this.getLabelOptionId(selectedLabel);
    this.labelSearchCtrl.setValue(selectedLabel?.name ?? '', { emitEvent: false });
  }

  goToLabelsPage(): void {
    this.router.navigate(['/labels']);
  }

  getLabelOptionId(label: any): any {
    return label?.id ?? label?.labelId ?? null;
  }

  /**
   * Handles changes in the workflow configuration when the component is in edit mode.
   */
  editModeWfChange() {
    // If the component is in edit mode, build the form with the existing workflow configuration object
    this.buildForm();
    if (this.isEditable) {
      this.onWorkflowChange();
    }
  }

  /**
   * Updates the add mode status and emits events accordingly.
   * - Emits an event to toggle the 'isAddConfig' status.
   * - If the component is editable, emits an event to reset the expandable state.
  */
  updateAddMode() {
    // Emits an event to toggle the 'isAddConfig' status (add mode status)
    this.isAddConfigEvent.emit(!this.isAddConfig)
    this.cancelUpdClicked.emit(false)
    // If the component is editable, emits an event to reset the expandable state (if any)
    if (this.isEditable) {
      this.isExpandable.emit(null)
    }
  }

  /**
   * Builds and initializes the 'workflowForm' FormGroup used for managing form controls.
   * Depending on the 'isEditable' flag, it sets up validators and initial values for the form fields.
  */
  buildForm() {
    // Create a FormGroup with form controls for workflow-related data
    this.workflowForm = this.fb.group({
      wfhId: [this.workflowConfigObj?.wfhId ? this.workflowConfigObj?.wfhId : '', !this.isEditable ? Validators.required : ''],
      wfId: [this.element?.value ? this.element.value : ''],
      cngName: ['', !this.isEditable ? [Validators.required, Validators.maxLength(150)] : ''],
      toolsAndThresholdArray: this.fb.array([]), // An array for dynamic form controls (empty for now)
      argumentsArray: this.fb.array([]), // Another array for dynamic form controls (empty for now)
    });
    this.updQgCheckFlag = this.workflowConfigObj?.updQgCheck || false;
    this.isSoleFlag = this.workflowConfigObj?.isSole || false;
  }

  /**
    * Getter method to access the 'argumentsArray' FormArray from the 'workflowForm'.
    * This allows you to conveniently work with the FormArray for managing workflow arguments.
    * @returns {FormArray} The 'argumentsArray' FormArray.
  */
  get argumentsArray() {
    return this.workflowForm.get("argumentsArray") as FormArray;
  }
  /**
    * Getter method to access the 'toolsAndThresholdArray' FormArray from the 'workflowForm'.
    * This allows you to conveniently work with the FormArray for managing tools and thresholds.
    * @returns {FormArray} The 'toolsAndThresholdArray' FormArray.
  */
  get toolsAndThresholdArray() {
    return this.workflowForm.get("toolsAndThresholdArray") as FormArray;
  }

  /**
    * Creates and initializes a FormGroup for managing form controls related to an input row.
    * @param {any} row - The input row data used to set initial values.
    * @returns {FormGroup} A FormGroup for managing the input row's form controls.
  */
  inputGroup(row: any): FormGroup {
    return this.fb.group({
      type: [row?.type ? row?.type : ''],
      default: [row?.default ? row?.default : ''],// Control for default value
      description: [row?.description ? row?.description : ''],// Control for description
      from: [row?.from ? row?.from : ''],// Control for 'from' value
      key: [row?.key ? row?.key : ''],// Control for key
      name: [row?.name ? row?.name : ''],// Control for name
      value: [row?.value ? row?.value : '', row?.required && !this.commonService.isJSONObject(row.default) ? Validators.required : ''],// Control for value with optional required validator
      required: [row?.required ? row?.required : '']// Control for 'required' status
    });
  }

  /**
    * Creates and initializes a FormGroup for managing form controls related to a threshold row.
    * @param {any} row - The threshold row data used to set initial values.
    * @returns {FormGroup} A FormGroup for managing the threshold row's form controls.
  */
  thresholdGroup(row: any): FormGroup {
    return this.fb.group({
      alwFail: [row?.alwFail],// Control for 'alwFail' checkbox
      checkbox: [row?.checkbox],// Control for a checkbox (unspecified purpose)
      id: [row?.id ? row.id : ''],// Control for ID
      name: [row?.name ? row.name : ''],// Control for name
      threshold: [row.threshold],// Control for threshold value
      operator: [row?.operator ? row?.operator : ''],
      tool: [row?.tool ? row?.tool : ''],
      desc: [row?.desc ? row?.desc : '']
    })
  }

  /**
   * Fetches available workflows from the ConfigBuilderService and updates the 'workflowData' property.
   * The fetched data is stored in the 'workflowData' property for use in the component.
  */
  getAllWorkFlows() {
    // Make an API call to the ConfigBuilderService to get available workflows
    this.configBuilderService.getAvailableWorkflows().subscribe((response) => {
      // Update the 'workflowData' property with the data received from the API
      this.workflowData = response.data.dbResp[0]['workflows'];
    })
  }

  /**
  * Adds a new workflow configuration.
  * - If the tour is starting, shows a success message and emits an event to toggle 'isAddConfig'.
  * - Otherwise, performs the following steps:
  *   1. Extracts trigger form values.
  *   2. Checks if there is an active configuration and removes it if found.
  *   3. Inserts the workflow configuration into the database.
  *   4. Saves trigger component data.
  *   5. Emits an event to update the component with the new configuration data.
  */
  addConfig() {
    // If the tour is starting, show a success message and emit an event to toggle 'isAddConfig'
    if (this.tourGuideService.isStartTour) {
      this.toastService.showSuccess(
        '',
        'Workflow configuration saved !!'
      );
      this.isAddConfigEvent.emit(!this.isAddConfig);
      return;
    }

    // Extract trigger form values
    let triggerFormVal: any = { ...this.triggerComp.formValue, updQgCheck: this.updQgCheckFlag, isSole: this.isSoleFlag, event: this.triggerComp.githubEvents.find((el => el.id == this.triggerComp.formValue?.event)) };

    let wFFormValue = {
      ...this.workflowForm.value,
      argumentsArray: this.getargInputs() || {},
      labelId: this.selectedLabelId ?? null
    }

    // Check if there is an active configuration and remove it if found
    this.configBuilderService.checkIsActive(this.wfhIdFromDropdown, triggerFormVal)
      .then((id) => {
        if (id > 0) {
          this.removeInactiveEvent.emit(id);
        }
        // Insert the workflow configuration into the database
        this.configBuilderService.insertWorkflow(id, { ...wFFormValue, ...triggerFormVal }, this.wfhIdFromDropdown, this.issueTmplId)
          .then(value => {
            // Save trigger component data
            this.triggerComp.saveData();
            // Emit an event to update the component with the new configuration data
            this.addModeUpdateEvent.emit(value);
            this.isAddConfigEvent.emit(!this.isAddConfig);
          })
          .catch();
      })
      .catch(() => undefined);
  }

  /**get input arguments and return args object */
  getargInputs() {

    // If no arguments are present, return an empty object
    if (this.workflowForm?.value?.argumentsArray?.length === 0) return {};

    let args: any = {}
    let tempArr = [...this.workflowForm.value.argumentsArray]

    // If issue template inputs
    if (this.issueTmplInputs.length > 0) {
      // Filter out inputs that are not part of the issue template
      this.issueTmplInputs.forEach((el: any) => {
        tempArr = tempArr.filter((el1) => el1.key != el.key)
      })

      // Loop through issue template inputs and add them to args
      this.issueTmplInputs.forEach((el: any) => {
        const value = this.workflowForm.value.argumentsArray.find((a: any) => a.key == el.id).value;
        args[el.id] = typeof value === 'string' ? value : JSON.stringify(value);
      });

      let finalArgs: any = {}
      // Loop through inputArgumentList and check if the default value is a JSON object
      this.inputArgumentList.forEach((el: any) => {
        if (this.commonService.isJSONObject(el.default)) {

          const jsonSource = el.default;
          let tempArr: any = {};

          // Iterate over the entries of the JSON object
          Object.entries(JSON.parse(jsonSource))?.map((obj => {
            // Check if the key exists in args and assign its value to tempArr
            if (args.hasOwnProperty(obj[0].toUpperCase())) {
              tempArr[obj[0]] = args[obj[0].toUpperCase()];
            } else if (args.hasOwnProperty(obj[0].toLowerCase())) {
              tempArr[obj[0]] = args[obj[0].toLowerCase()];
            }
          }))
          // Convert tempArr to a JSON string and assign it to finalArgs
          finalArgs[el.key] = JSON.stringify(tempArr);
        } else {
          // If the key is not in inputArgumentList or the default value is not a JSON object, assign the value from args to finalArgs
          finalArgs[el.key] = args[el.key];
        }
      })
      return finalArgs;
    }

    // Filter out inputs that are already in the inputArgumentList
    this.inputArgumentList.forEach((el: any) => {
      tempArr = tempArr.filter((el1) => el1.key != el.key)
    })

    // Loop through the inputArgumentList and add them to args
    this.inputArgumentList.forEach((el: any) => {
      if (![Keys.runnerDetails].includes(el.key.toLowerCase())) {
        if (this.commonService.isJSONObject(el.default)) {
          args[el.key] = JSON.stringify(tempArr.reduce((acc: any, item: any) => {
            acc[item.key] = item.value;
            return acc;
          }, {}))
        } else {
          const value = this.workflowForm.value.argumentsArray.find((a: any) => a.key == el.key).value;
          args[el.key] = typeof value === 'string' ? value : JSON.stringify(value);
        }
      } else {
        const value = this.workflowForm.value.argumentsArray.find((a: any) => a.key == el.key).value;
        args[el.key] = typeof value === 'string' ? value : JSON.stringify(value);
      }
    });
    return args
  }

  /**
   * Checks for conflicting active workflows only when saving an active configuration,
   * then updates the workflow based on the response.
   */
  checkActiveAndUpdateConfig() {
    const effectiveStatus = (this.cngStatus === '' || this.cngStatus == null) ? this.element.active : this.cngStatus;
    if (effectiveStatus !== true && effectiveStatus !== 'true') {
      this.updateConfig();
      return;
    }

    // Extract trigger form values
    let triggerFormVal: any = { ...this.triggerComp.formValue, event: this.triggerComp.githubEvents.find((el => el.id == this.triggerComp.formValue?.event)) };

    // Check if there is an active configuration and remove it if found
    this.configBuilderService.checkIsActive(this.workflowConfigObj.wfhId, triggerFormVal, this.element.value)
      .then((id) => {
        if (id > 0) {
          this.removeInactiveEvent.emit(id);
        }
        this.updateConfig(id);
      })
      .catch(() => undefined);
  }

  /**
   * updates the workflow config
   * 
   * @param disable workflow id to make inactive
   */
  private updateConfig(disable: number = -1) {

    let wFFormValue = {
      ...this.workflowForm.value,
      updQgCheck: this.updQgCheckFlag != this.workflowConfigObj.updQgCheck ? this.updQgCheckFlag : null,
      isSole: this.isSoleFlag !== (this.workflowConfigObj.isSole ?? false) ? this.isSoleFlag : null,
      argumentsArray: this.getargInputs() || {},
      labelId: this.selectedLabelId ?? null
    }

    this.cancelUpdClicked.emit(false)
    this.configBuilderService.updateWorkflowConfig(disable, {
      ...wFFormValue, ...this.triggerComp.changedFormValue, 'cngName': this.cngName,
      'status': this.cngStatus
    }, this.issueTmplId)
      .then(() => {
        this.triggerComp.saveData()
        this.configBuilderService.triggerGetWfDetails(true);
        this.configBuilderService.getWorkRepoNames(true);
      })
      .catch(() => undefined);
    this.isExpandable.emit(null);
  }

  /**
   * this method updates the state of the class based on the selected workflow,
   * retrieves the configuration details for the selected workflow,
   * and updates the configData property of the class with the retrieved data.
   */
  async onWorkflowChange(data?: any) {
    this.configHide = false;
    this.inputArgumentList = [];
    /**manualWf object is used in the condition it will be triggered when user is on manual workflow trigger page*/
    if (data?.value && !this.isEditable || this.manualWf?.isManualWfTrigger) {
      // If element contains repoUrl and branch, set workflowDtls
      if (this.element?.repoUrl && this.element?.branch) {
        this.workflowDtls = [this.element?.repoUrl, this.element?.branch];
      }

      /**
       * /github-workflows/execute?jobId
       * Call an api to get input arguments from wkfl_hub as well as wkfl_job table
       * Defined input arguments and modified input arguments
       */
      if (this.targetExec && this.jobId) {
        this.configBuilderService.getInputArgsFromWfjobWfHub(this.jobId).subscribe((response: any) => {
          if (response) {
            const jobCngInpArgs = response?.data?.dbResp?.cng?.wkfl_input;
            const cngInpArgs = response?.data?.dbResp?.cng?.wkfl_hub_cfg?.inputs_arguments;

            const WfHubQualGate = response?.data?.dbResp?.cng?.qual_gate;
            const WfJobQualGate = response?.data?.dbResp?.cng?.qual_gate_rule;

            // Quality gates merge for wkfl_hub and wkfl_job
            this.qualGateMapping(WfHubQualGate, WfJobQualGate);
            this.inputArgsMapping(jobCngInpArgs, cngInpArgs);
          }
        })
        return;
      }

      // If the data contains a value or element contains id, fetch workflow configuration details
      this.configBuilderService.getWorkflowConfigDetails(data?.value || this.element?.id).subscribe((response: any) => {
        this.inputArgumentList = response?.data?.dbResp?.cng?.inputs_arguments?.map((el: any) => {
          return { ...el, value: el.default }
        })?.filter((inpArg: any) => inpArg?.from != 'generated')
      })
    }

    let response = await this.configBuilderService.getQualityGates(data?.value || this.element?.id || this.workflowForm.value.wfhId).toPromise();
    let threshList = response?.data?.dbResp || [];

    if (this.isEditable && !this.element?.id) {
      this.inputArgumentList = this.workflowConfigObj?.arguments
      let resp: any = await this.configBuilderService.getWorkflowQualityGates(this.workflowForm.value.wfId).toPromise()
      let wfThreshList = resp?.data?.dbResp || [];
      this.qualGateMapping(threshList, wfThreshList);
      return;
    }
    this.thresholdList = threshList.map((el: any) => { el = { ...el, alwFail: true, operator: "<" }; return el; })
  }

  /**
   * Input arguments mapping based on workflow hub input args and workflow job input args
   * and mapping it
   * @param jobCngInpArgs 
   * @param cngInpArgs 
   */
  inputArgsMapping(jobCngInpArgs: any, cngInpArgs: any) {
    if (cngInpArgs) {
      jobCngInpArgs = jobCngInpArgs || {};
      this.inputArgumentList = cngInpArgs?.map((argument: any) => {
        argument.default = argument?.default === undefined ? '' : argument?.default;

        const defaultValue = jobCngInpArgs[argument.key] !== undefined ? jobCngInpArgs[argument.key] : argument?.default;
        return {
          ...argument,
          default: defaultValue,
          value: defaultValue
        }
      }).filter((inpArg: any) => inpArg.from !== 'generated');
    }
  }

  /**
   * Map quality gates with the exeucted quality gates
   * @param threshList 
   * @param wfThreshList 
   */
  qualGateMapping(threshList: any, wfThreshList: any) {
    // Map through the threshold list and find matching thresholds in the workflow threshold list
    this.thresholdList = threshList?.map((t: any) => {
      // Find a threshold in the workflow threshold list with a matching name
      const existingThreshold = wfThreshList?.find((el: any) => el.name === t.name);

      return existingThreshold ?
        { ...existingThreshold, checkbox: true, alwFail: existingThreshold?.alwFail || true, operator: existingThreshold?.operator || "<" } :
        { ...t, alwFail: t?.alwFail || true, operator: t?.operator || "<" };
    });
  }

  /**
   * Retrieves and returns a validation error message for the 'cngName' field in the workflow form.
   * - If the field is required, it returns the required message.
   * - If the field has exceeded its maximum length, it returns the maximum length message.
   * - If there are no errors, it returns an empty string.
   * @returns {string} The validation error message for the 'cngName' field.
   */
  getConfigurationName() {
    if (this.workflowForm.controls['cngName'].errors?.['required']) {
      // If 'cngName' is required, return the required message
      return Messages.addConfigNew.cfgName.required;
    }
    else if (this.workflowForm.controls['cngName'].errors?.['maxlength']) {
      // If 'cngName' has exceeded its maximum length, return the maximum length message
      return Messages.addConfigNew.cfgName.maxLength;
    }

    // If there are no errors, return an empty string
    return this.workflowForm.controls['cngName'].errors ? Messages.addConfigNew.cfgName.invalid : '';
  }

  /**
   * Adds input arguments to the 'argumentsArray' FormArray in the workflow form.
   * @param {any} inputArgs - An object containing input argument data and a flag indicating if they are valid.
   */
  addInputArgs(inputArgs: any) {
    let { inputArgArray, isArgValid, issueTmplData, isIssueTmplValid } = inputArgs
    // Set the 'isInputArgValid' flag based on the provided value
    this.isInputArgValid = isArgValid;
    this.isIssueTmplValid = isIssueTmplValid;

    // Remove the existing 'argumentsArray' control from the workflow form
    this.workflowForm.removeControl('argumentsArray');

    // Add a new 'argumentsArray' control as a FormArray to the workflow form
    this.workflowForm.addControl('argumentsArray', new FormArray([]))

    // Based on object received loop through the array and add each argument as a FormGroup
    if (inputArgArray) {
      // Loop through the input argument array and add each argument as a FormGroup to the 'argumentsArray'
      inputArgArray?.forEach((el: any) => {
        this.argumentsArray.push(this.inputGroup(el));
      });
    } else {
      // Loop through the issue template argument array and add each argument as a FormGroup to the 'argumentsArray'
      issueTmplData?.forEach((el: any) => {
        this.argumentsArray.push(this.inputGroup(el));
      });
    }
  }

  /**
   * Adds threshold data to the 'toolsAndThresholdArray' FormArray in the workflow form.
   * @param {any} thresholds - An array of threshold data to be added.
  */
  addThreshHold(thresholds: any) {
    if (Array.isArray(thresholds)) {
      // Remove the existing 'toolsAndThresholdArray' control from the workflow form
      this.workflowForm.removeControl('toolsAndThresholdArray');
      // Add a new 'toolsAndThresholdArray' control as a FormArray to the workflow form
      this.workflowForm.addControl('toolsAndThresholdArray', new FormArray([]))
      // Iterate through the thresholds array and add each threshold as a FormGroup to the 'toolsAndThresholdArray'
      thresholds.forEach((el: any) => {
        this.toolsAndThresholdArray.push(this.thresholdGroup(el))
      });
      return;
    }
    // If 'thresholds' is not an array, add a single threshold value as 'toolsAndThreshold' control to the form
    this.workflowForm.addControl('toolsAndThreshold', new FormControl(thresholds))
  }

  /**
   * Computes and returns a boolean value indicating whether the entire workflow form is valid.
   * It checks both the validity of the 'workflowForm' itself and the 'isTriggerFormValid' flag.
   * @returns {boolean} True if both the workflow form and the trigger form are valid, otherwise false.
   * 
   * if manualWf.isManualWfTrigger then only check workflow form
   */
  /**
   * A labeled check must be selected before saving/updating a config.
   * Not applicable to manual workflow triggers (the Labeled Checks tab is hidden there).
   */
  get isLabeledCheckMissing(): boolean {
    if (this.manualWf?.isManualWfTrigger) {
      return false;
    }
    return this.selectedLabelId === null || this.selectedLabelId === undefined || this.selectedLabelId === '';
  }

  get isValid() {
    if (this.isIssueTmplChecked && this.issueTmplInputs.length === 0) {
      return false;
    }
    if (this.tourGuideService.isStartTour) {
      return true;
    } else if (this.cngNameUpdValid != undefined) {
      return this.manualWf?.isManualWfTrigger ? this.workflowForm.valid : this.workflowForm.valid && this.isTriggerFormValid && this.cngNameUpdValid
    }
    else {
      return this.manualWf?.isManualWfTrigger ? this.workflowForm.valid : this.workflowForm.valid && this.isTriggerFormValid
    }
  }

  /**
   * Checks if the workflow form is valid for manual trigger.
   */
  get isValidManualTrigger() {
    return this.workflowForm.valid;
  }

  /**
   * Converts an AbstractControl to a FormControl.
   * @param absCtrl The AbstractControl to convert.
   * @returns The converted FormControl.
   */
  convertToFormControl(absCtrl: AbstractControl | null): FormControl {
    const ctrl = absCtrl as FormControl;
    return ctrl;
  }

  /**
   * click event when in config builder checkActiveAndUpdateConfig will be triggered
   * when in manual workflow page then manualTrigger will be performed
   * @returns function to be executed based on the condition
   */
  updateTrigger() {
    return this.manualWf?.isManualWfTrigger ? this.manualTrigger() : this.checkActiveAndUpdateConfig();
  }

  /**
   * manualTrigger is used to trigger the manual workflow by checking the form values and calling the
   * trigger api
   */
  manualTrigger() {
    const jsonObj = {
      'input': this.getargInputs(),
      'qgRules': this.workflowForm.value.toolsAndThreshold?.added || []
    };

    /**Api call */
    this.configBuilderService.manualWfTrigger(this.element.id, this.element.branch, jsonObj).subscribe((resp: any) => {
      this.isExpandable.emit(null);
      this.toastService.showSuccess(Messages.success, Messages.manualWfTrigger.triggerSuccess);
    }, (errors: any) => {
      this.isExpandable.emit(null);
      this.toastService.showError(Messages.failed, Messages.manualWfTrigger.triggerFail)
    })
  }

  /**
   * Updates the value of the changed event status.
   * @param val The new value for the changed event status.
   */
  eventChange(val: any) {
    this.changeEventStatus = val;
  }

  /**
   * Updates the value of the changed event.
   * @param val The new value for the changed event.
   */
  changeEventValue(val: any) {
    this.changedEventValue = val;
  }

  /** Issue template implementation */

  /**
   * Handles the issue template checkbox event.
   * If the checkbox is unchecked, it resets the issue template inputs and ID.
   * @param event The event indicating whether the issue template checkbox is checked or not.
   */
  isIssueTmpl(event: boolean) {
    if (!event) {
      this.issueTmplInputs = []; // Reset issue template inputs if unchecked
      this.issueTmplId = null; // Reset issue template ID if unchecked
      // When unchecking issue template, reset input arguments to use default values
      this.resetInputArgumentsToDefaults();
    }
    // You can also assign the value to a property if needed
    this.isIssueTmplChecked = event;
  }

  /**
   * Resets input arguments to use default values instead of current values
   */
  resetInputArgumentsToDefaults() {
    if (this.inputArgumentList && Array.isArray(this.inputArgumentList)) {
      this.inputArgumentList = this.inputArgumentList.map((input: any) => ({
        ...input,
        value: input.default // Use default value instead of current value
      }));
    }
  }

  /**
   * Handles the issue template table event.
   * @param event The event containing the issue template inputs and ID.
   */
  issueTmplTable(event: any) {
    this.issueTmplInputs = [];
    this.issueTmplInputs = event?.issueTmplInp;
    this.issueTmplId = event?.id ?? null;
  }

  /**
   * Handles the issue template input event.
   * Updates the issue template inputs and ID based on the event data.
   * @param event The event containing issue template inputs and ID.
   */
  getIssueTempInput(event: any) {
    this.issueTmplInputs = event?.issueTempInp;
    this.issueTmplId = event.issueTempId ?? null;
  }

  /**
   * Prerequisite implementation
   */
  openPrerequisite() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/workflow-prerequisite']));
    window.open(url, '_blank');
  }
}
